import React from 'react';
import type { WorksheetVisualKind } from '../../data/content';
import { problemSteps } from '../../data/content';
type WorksheetVisualProps = {
  kind: WorksheetVisualKind;
};
const NAVY = '#0b1f4b';
const BLUE = '#1d5bd8';
const RED = '#e11d48';
const GREEN = '#16a34a';
const ORANGE = '#f97316';
const VIOLET = '#7c3aed';
const YELLOW = '#ffc933';
export function WorksheetVisual({
  kind
}: WorksheetVisualProps) {
  switch (kind) {
    case 'tenFrame':
      return <TenFrame data-id="element-290" data-mp="36i22sgvhlwb8" />;
    case 'shapes':
      return <Shapes data-id="element-291" data-mp="3bfc0ejt15ea5" />;
    case 'units':
      return <Units data-id="element-292" data-mp="3bgdbgoa1q0rv" />;
    case 'fractions':
      return <Fractions data-id="element-293" data-mp="2wi1hwqhyz84x" />;
    case 'balance':
      return <Balance data-id="element-294" data-mp="2wjck4lvro7tx" />;
    case 'charts':
      return <Charts data-id="element-295" data-mp="31gw482f5bets" />;
    case 'probability':
      return <Probability data-id="element-296" data-mp="31hnt6ue8meb3" />;
    case 'steps':
      return <Steps data-id="element-297" data-mp="uqeut0177xdq" />;
  }
}
function TenFrame() {
  return <svg viewBox="0 0 260 150" className="h-full w-full" role="img" aria-label="Zehnerfeld mit 8 Punkten und Zahlenstrahl 8 + 5 = 13" data-id="element-298" data-mp="urpwmub32v27">
      {Array.from({
      length: 10
    }).map((_, i) => {
      const x = 40 + i % 5 * 36;
      const y = 10 + Math.floor(i / 5) * 36;
      return <g key={i} data-id="element-299" data-mp="1f7rzkym4cdu8" data-mpi={i}>
            <rect x={x} y={y} width="34" height="34" rx="4" fill="#fff" stroke={NAVY} strokeWidth="1.5" data-id="element-300" data-mp="1f8jnroddz9ah" />
            {i < 8 && <circle cx={x + 17} cy={y + 17} r="11" fill={RED} data-id="element-301" data-mp="1tzsmqo36i70a" />}
            {i >= 8 && <circle cx={x + 17} cy={y + 17} r="11" fill={BLUE} data-id="element-302" data-mp="1oz4u5xzaxi3y" />}
          </g>;
    })}
      <line x1="20" y1="120" x2="240" y2="120" stroke={NAVY} strokeWidth="1.5" data-id="element-303" data-mp="119ka1brewet3" />
      {[8, 9, 10, 11, 12, 13].map((n, i) => <g key={n} data-id="element-304" data-mp="wcac17pyiutn" data-mpi={i}>
          <line x1={40 + i * 36} y1="115" x2={40 + i * 36} y2="125" stroke={NAVY} strokeWidth="1.5" data-id="element-305" data-mp="1a9guf16rqric" />
          <text x={40 + i * 36} y="140" fontSize="10" fontWeight="700" textAnchor="middle" fill={NAVY} data-id="element-306" data-mp="168dc88190jdy">
            {n}
          </text>
        </g>)}
      <path d="M40 112 Q76 84 112 112" fill="none" stroke={RED} strokeWidth="2" data-id="element-307" data-mp="2ln2hmgt2ij18" />
      <text x="76" y="92" fontSize="10" fontWeight="800" textAnchor="middle" fill={RED} data-id="element-308" data-mp="2hmik3c4l1dek">+2</text>
      <path d="M112 112 Q166 78 220 112" fill="none" stroke={BLUE} strokeWidth="2" data-id="element-309" data-mp="1ebl8fbuux9ia" />
      <text x="166" y="88" fontSize="10" fontWeight="800" textAnchor="middle" fill={BLUE} data-id="element-310" data-mp="1jaelx4a590ig">+3</text>
    </svg>;
}
function Shapes() {
  const items = [{
    label: 'Kreis',
    info: '0 Ecken',
    shape: <circle cx="30" cy="34" r="24" fill={BLUE} data-id="element-311" data-mp="1scfjk29ahokn" />
  }, {
    label: 'Dreieck',
    info: '3 Ecken',
    shape: <polygon points="30,8 56,58 4,58" fill={RED} data-id="element-312" data-mp="vmodr6vjsezj" />
  }, {
    label: 'Quadrat',
    info: '4 Ecken',
    shape: <rect x="7" y="10" width="46" height="46" rx="3" fill={GREEN} data-id="element-313" data-mp="10k6p13x1f6ap" />
  }, {
    label: 'Rechteck',
    info: '4 Ecken',
    shape: <rect x="0" y="18" width="60" height="32" rx="3" fill={YELLOW} data-id="element-314" data-mp="14lrw28mw4qcz" />
  }];
  return <div className="grid h-full grid-cols-4 gap-2" data-id="element-315" data-mp="19djs2yotfoai">
      {items.map(item => <div key={item.label} className="flex flex-col items-center justify-center rounded-lg border border-slate-200 p-1.5" data-id="element-316" data-mp="cvx6qbz190ob">
          <svg viewBox="0 0 60 66" className="h-12 w-12 sm:h-16 sm:w-16" aria-hidden="true" data-id="element-317" data-mp="gvo5p2ypqn3x">
            {item.shape}
          </svg>
          <span className="mt-1 text-[10px] font-bold text-navy sm:text-xs" data-id="element-318" data-mp="9bdhcu62lhrv">{item.label}</span>
          <span className="text-[9px] text-navy/60 sm:text-[10px]" data-id="element-319" data-mp="4cacb9q3bbjw">{item.info}</span>
        </div>)}
    </div>;
}
function Units() {
  const rows = [{
    big: '1 km',
    small: '1000 m',
    color: 'bg-brand'
  }, {
    big: '1 m',
    small: '100 cm',
    color: 'bg-brand'
  }, {
    big: '1 kg',
    small: '1000 g',
    color: 'bg-leaf'
  }, {
    big: '1 l',
    small: '1000 ml',
    color: 'bg-flame'
  }];
  return <table className="w-full border-separate border-spacing-y-1.5 text-[11px] sm:text-xs" data-id="element-320" data-mp="3hr6ncf2rzauo">
      <thead data-id="element-321" data-mp="3cug81tysojab">
        <tr className="text-left text-[9px] uppercase tracking-wide text-navy/60 sm:text-[10px]" data-id="element-322" data-mp="3cru2u0zdmfv9">
          <th className="font-bold" data-id="element-323" data-mp="38qs0hba5xnp2">Größere Einheit</th>
          <th className="text-center font-bold" data-id="element-324" data-mp="33sgu5aj2tvbl">×</th>
          <th className="font-bold" data-id="element-325" data-mp="2yuf8chu9nhwf">Kleinere Einheit</th>
        </tr>
      </thead>
      <tbody data-id="element-326" data-mp="2ywi6jp5kohc8">
        {rows.map(row => <tr key={row.big} data-id="element-327" data-mp="12r0prbadkof4">
            <td data-id="element-328" data-mp="12rsm4obsqq6g">
              <span className={`inline-block rounded px-2 py-1 font-bold text-white ${row.color}`} data-id="element-329" data-mp="12tdi8idreh6f">{row.big}</span>
            </td>
            <td className="text-center font-bold text-navy/60" data-id="element-330" data-mp="t1qwvt7pj1py">→</td>
            <td data-id="element-331" data-mp="szd4js4ew3m3">
              <span className="inline-block rounded bg-ice px-2 py-1 font-bold text-navy" data-id="element-332" data-mp="y0kcbr6k12sr">{row.small}</span>
            </td>
          </tr>)}
      </tbody>
    </table>;
}
function Fractions() {
  return <svg viewBox="0 0 260 150" className="h-full w-full" role="img" aria-label="Kreis und Balkenmodell zeigen drei Viertel" data-id="element-333" data-mp="o24ihgg57ffk">
      <circle cx="60" cy="70" r="46" fill="#fff" stroke={NAVY} strokeWidth="1.5" data-id="element-334" data-mp="a3n100gtyz63" />
      <path d="M60 70 L60 24 A46 46 0 1 1 14 70 Z" fill={RED} data-id="element-335" data-mp="f16hz84wxq1q" />
      <line x1="60" y1="24" x2="60" y2="116" stroke={NAVY} strokeWidth="1.5" data-id="element-336" data-mp="f3j5e0tfwsm6" />
      <line x1="14" y1="70" x2="106" y2="70" stroke={NAVY} strokeWidth="1.5" data-id="element-337" data-mp="24hfkmmssc4rt" />
      <text x="60" y="140" fontSize="11" fontWeight="800" textAnchor="middle" fill={NAVY} data-id="element-338" data-mp="29eegb9s3kjs4">3/4</text>
      {[0, 1, 2, 3].map(i => <rect key={i} x={130 + i * 28} y="40" width="28" height="30" fill={i < 3 ? BLUE : '#fff'} stroke={NAVY} strokeWidth="1.5" data-id="element-339" data-mp="3tvf7muxc1xvm" />)}
      <text x="186" y="92" fontSize="11" fontWeight="800" textAnchor="middle" fill={NAVY} data-id="element-340" data-mp="7ifb2t9nx1t1">3 von 4 Teilen</text>
      <text x="186" y="122" fontSize="22" fontWeight="800" textAnchor="middle" fill={BLUE} data-id="element-341" data-mp="2qfg7f8g8z3b">¾</text>
    </svg>;
}
function Balance() {
  return <svg viewBox="0 0 260 150" className="h-full w-full" role="img" aria-label="Waagenmodell: x + 3 = 7" data-id="element-342" data-mp="gkoktgx3wr5d">
      <polygon points="130,60 115,130 145,130" fill={NAVY} data-id="element-343" data-mp="bm4zniqc7je2" />
      <rect x="30" y="54" width="200" height="6" rx="3" fill={NAVY} data-id="element-344" data-mp="qgj6ym0jh4cw" />
      <line x1="55" y1="60" x2="55" y2="80" stroke={NAVY} strokeWidth="1.5" data-id="element-345" data-mp="lhoq3pm4tpvf" />
      <line x1="205" y1="60" x2="205" y2="80" stroke={NAVY} strokeWidth="1.5" data-id="element-346" data-mp="2s84or2ymqhk5" />
      <rect x="20" y="80" width="70" height="6" rx="3" fill={NAVY} data-id="element-347" data-mp="2sa91maeiaqwk" />
      <rect x="170" y="80" width="70" height="6" rx="3" fill={NAVY} data-id="element-348" data-mp="2naw3gti6pxeo" />
      <rect x="26" y="58" width="22" height="22" rx="4" fill={VIOLET} data-id="element-349" data-mp="3cy8lzhr622ni" />
      <text x="37" y="73" fontSize="12" fontWeight="800" textAnchor="middle" fill="#fff" data-id="element-350" data-mp="3gz2bugo65rj1">x</text>
      {[0, 1, 2].map(i => <circle key={i} cx={58 + i * 11} cy="74" r="5" fill={YELLOW} stroke={NAVY} strokeWidth="1" data-id="element-351" data-mp="3lxvm8085t2ez" />)}
      {Array.from({
      length: 7
    }).map((_, i) => <circle key={i} cx={184 + i % 4 * 12} cy={i < 4 ? 74 : 63} r="5" fill={YELLOW} stroke={NAVY} strokeWidth="1" data-id="element-352" data-mp="3quvl9r6ld4wl" data-mpi={i} />)}
      <text x="130" y="20" fontSize="14" fontWeight="800" textAnchor="middle" fill={NAVY} data-id="element-353" data-mp="3s0g2p5tu0gw">x + 3 = 7</text>
      <text x="55" y="104" fontSize="10" fontWeight="700" textAnchor="middle" fill={RED} data-id="element-354" data-mp="8iz2m6herusl">− 3</text>
      <text x="205" y="104" fontSize="10" fontWeight="700" textAnchor="middle" fill={RED} data-id="element-355" data-mp="dilfufx8lb1e">− 3</text>
      <text x="130" y="146" fontSize="12" fontWeight="800" textAnchor="middle" fill={VIOLET} data-id="element-356" data-mp="ii7soo95kp9o">x = 4</text>
    </svg>;
}
function Charts() {
  const bars = [40, 70, 55, 90, 65];
  return <svg viewBox="0 0 260 150" className="h-full w-full" role="img" aria-label="Säulendiagramm, Kreisdiagramm und Liniendiagramm" data-id="element-357" data-mp="27z3cuihq530v">
      <line x1="16" y1="130" x2="130" y2="130" stroke={NAVY} strokeWidth="1.5" data-id="element-358" data-mp="22yo1txkx07m2" />
      <line x1="16" y1="20" x2="16" y2="130" stroke={NAVY} strokeWidth="1.5" data-id="element-359" data-mp="1xzkwsd4xq1e3" />
      {bars.map((h, i) => <rect key={i} x={24 + i * 21} y={130 - h} width="15" height={h} rx="2" fill={[BLUE, ORANGE, GREEN, VIOLET, RED][i]} data-id="element-360" data-mp="1t2ciwoqtkjin" data-mpi={i} />)}
      <circle cx="200" cy="48" r="32" fill={BLUE} data-id="element-361" data-mp="2qr4grz9cvjh5" />
      <path d="M200 48 L200 16 A32 32 0 0 1 230 58 Z" fill={ORANGE} data-id="element-362" data-mp="2lpowwaiwzj0c" />
      <path d="M200 48 L230 58 A32 32 0 0 1 180 73 Z" fill={GREEN} data-id="element-363" data-mp="2gz8gchjsrh9r" />
      <polyline points="150,130 172,112 194,118 216,96 240,100" fill="none" stroke={VIOLET} strokeWidth="2.5" data-id="element-364" data-mp="2gxf7qwpmimz4" />
      {[[150, 130], [172, 112], [194, 118], [216, 96], [240, 100]].map(([x, y]) => <circle key={x} cx={x} cy={y} r="3" fill={VIOLET} data-id="element-365" data-mp="39gueov8f0lda" />)}
    </svg>;
}
function Probability() {
  return <svg viewBox="0 0 260 150" className="h-full w-full" role="img" aria-label="Würfel, Münze und Glücksrad" data-id="element-366" data-mp="34mzxpzk72khw">
      <rect x="14" y="36" width="64" height="64" rx="10" fill="#fff" stroke={NAVY} strokeWidth="2" data-id="element-367" data-mp="2hrpynjo5kxfn" />
      {[[30, 52], [62, 52], [46, 68], [30, 84], [62, 84]].map(([x, y]) => <circle key={`${x}-${y}`} cx={x} cy={y} r="5" fill={NAVY} data-id="element-368" data-mp="23yqjjisa958m" />)}
      <text x="46" y="124" fontSize="11" fontWeight="800" textAnchor="middle" fill={NAVY} data-id="element-369" data-mp="29068vn1htxwt">P = 1/6</text>
      <circle cx="130" cy="68" r="30" fill={YELLOW} stroke="#e0a800" strokeWidth="3" data-id="element-370" data-mp="2wq0g9p31mu9o" />
      <text x="130" y="74" fontSize="16" fontWeight="800" textAnchor="middle" fill={NAVY} data-id="element-371" data-mp="30qalv0mxbio0">€</text>
      <text x="130" y="124" fontSize="11" fontWeight="800" textAnchor="middle" fill={NAVY} data-id="element-372" data-mp="2mqjdbff6euhe">P = 1/2</text>
      <circle cx="214" cy="68" r="32" fill={BLUE} data-id="element-373" data-mp="2rokwe0clqtsv" />
      <path d="M214 68 L214 36 A32 32 0 0 1 246 68 Z" fill={RED} data-id="element-374" data-mp="1bc5w2texhdac" />
      <path d="M214 68 L246 68 A32 32 0 0 1 214 100 Z" fill={GREEN} data-id="element-375" data-mp="1bdz349tgdziv" />
      <path d="M214 68 L214 100 A32 32 0 0 1 182 68 Z" fill={ORANGE} data-id="element-376" data-mp="1gbs9p3yo4vjo" />
      <polygon points="214,30 208,20 220,20" fill={NAVY} data-id="element-377" data-mp="18qomshh8070k" />
      <circle cx="214" cy="68" r="4" fill="#fff" data-id="element-378" data-mp="18tatkewaeihq" />
      <text x="214" y="124" fontSize="11" fontWeight="800" textAnchor="middle" fill={NAVY} data-id="element-379" data-mp="13rbpf2sq3hjq">P = 1/4</text>
    </svg>;
}
function Steps() {
  const colors = ['bg-brand', 'bg-grape', 'bg-flame', 'bg-leaf', 'bg-cherry', 'bg-navy'];
  return <ol className="grid h-full grid-cols-3 gap-2" data-id="element-380" data-mp="1cqyr7ppd6pdb">
      {problemSteps.map((step, index) => <li key={step} className="flex flex-col items-center justify-center rounded-lg border border-slate-200 p-1.5 text-center" data-id="element-381" data-mp="p1e733hh5m2g" data-mpi={index}>
          <span className={`flex h-7 w-7 items-center justify-center rounded-full font-display text-xs font-extrabold text-white ${colors[index]}`} data-id="element-382" data-mp="yt0gcua95b2k">
            {index + 1}
          </span>
          <span className="mt-1.5 text-[9px] font-bold uppercase leading-tight text-navy sm:text-[10px]" data-id="element-383" data-mp="6czfof38w614">{step}</span>
        </li>)}
    </ol>;
}